import React from 'react';
import { Calendar, Newspaper, Info } from 'lucide-react';

const UpdatesLearningPage = () => {
  // Sample updates - in the future, this would come from a backend/CMS
  const updates = [
    {
      id: 1,
      date: '2025-12-15',
      type: 'platform',
      title: 'New Reading & Learning Section Added',
      summary: 'We\'ve added a new section with 8 educational articles covering topics like blood test trends, reference ranges, and why fatigue is complex. All written in plain language with no medical advice.',
      icon: Info,
      color: 'blue'
    },
    {
      id: 2,
      date: '2025-12-10',
      type: 'educational',
      title: 'Understanding Watch and Wait',
      summary: 'Watch and wait is a monitoring approach used when CLL is present but not causing significant problems. It\'s a time of observation, not inaction, where your healthcare team tracks your blood results and symptoms over time. Many people find this period emotionally challenging even when it\'s the medically appropriate approach.',
      icon: Newspaper,
      color: 'indigo'
    },
    {
      id: 3,
      date: '2025-12-01',
      type: 'platform',
      title: 'Mobile Chart Optimization Complete',
      summary: 'Charts on mobile devices have been optimized for better readability. Height reduced by 36% and gaps between charts and card edges minimized for a cleaner, more professional appearance.',
      icon: Info,
      color: 'blue'
    },
    {
      id: 4,
      date: '2025-11-28',
      type: 'awareness',
      title: 'Why Individual Tolerance Varies with Food',
      summary: 'When it comes to nutrition and CLL, what works for one person may not work for another. Some people find certain foods helpful; others notice no difference. This variability is normal and doesn\'t mean you\'re doing something wrong. If you\'re interested in dietary changes, discuss them with a registered dietitian who understands CLL.',
      icon: Newspaper,
      color: 'emerald'
    },
    {
      id: 5,
      date: '2025-11-20',
      type: 'platform',
      title: 'Common Questions Page Launched',
      summary: 'A new page with 12 frequently asked questions about CLL has been added. Topics include watch and wait, blood number fluctuations, reference ranges, and more. Each answer provides context without medical advice.',
      icon: Info,
      color: 'blue'
    }
  ];

  const getTypeLabel = (type) => {
    switch(type) {
      case 'platform':
        return 'Platform Update';
      case 'educational':
        return 'Educational Summary';
      case 'awareness':
        return 'General Awareness';
      default:
        return 'Update';
    }
  };

  const getTypeColors = (color) => {
    const colors = {
      blue: {
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: 'text-blue-600 bg-blue-50'
      },
      indigo: {
        badge: 'bg-indigo-100 text-indigo-700 border-indigo-200',
        icon: 'text-indigo-600 bg-indigo-50'
      },
      emerald: {
        badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        icon: 'text-emerald-600 bg-emerald-50'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Newspaper className="h-8 w-8 text-slate-700" />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Updates & Learning
          </h1>
        </div>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 max-w-3xl">
          Platform updates, educational summaries, and general awareness items. Written for context and clarity, not as medical guidance.
        </p>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <p className="text-sm font-medium text-amber-900 mb-2">
          Educational Context Only
        </p>
        <p className="text-sm text-amber-800">
          This section provides platform updates and educational context. It does not contain research claims, study interpretations, or treatment recommendations. Always discuss medical decisions with your healthcare provider.
        </p>
      </div>

      {/* Updates List */}
      <div className="space-y-6">
        {updates.map((update) => {
          const Icon = update.icon;
          const colors = getTypeColors(update.color);
          
          return (
            <div 
              key={update.id}
              className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-4 flex-1">
                  {/* Icon */}
                  <div className={`p-3 rounded-xl ${colors.icon} flex-shrink-0`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Type Badge */}
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full border mb-3 ${colors.badge}`}>
                      {getTypeLabel(update.type)}
                    </span>
                    
                    {/* Title */}
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                      {update.title}
                    </h2>
                    
                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(update.date).toLocaleDateString('en-GB', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Summary */}
              <div className="pl-0 md:pl-16">
                <p className="text-base md:text-lg leading-relaxed text-slate-700">
                  {update.summary}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8">
        <p className="text-base text-blue-900">
          <strong>Note:</strong> This section will be updated periodically with new platform features, educational summaries, and general awareness items. Check back from time to time to see what's new.
        </p>
      </div>
    </div>
  );
};

export default UpdatesLearningPage;
